<template>
  <v-dialog :value="value" max-width="920" persistent scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="font-weight-bold primary--text">
        Completar dados cadastrais
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-6">
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.name" label="Nome completo" outlined dense
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.phone_number" label="Telefone" outlined dense
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="form.nationality" :items="countries" item-text="nome_pais" item-value="sigla"
                label="Nacionalidade" outlined dense :rules="[$rules.required]" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field :value="formattedRegistration" label="CPF/CNPJ" outlined dense disabled />
            </v-col>
          </v-row>

          <v-divider class="mb-5" />

          <div class="subtitle-1 font-weight-bold mb-3">Endereço</div>
          <v-switch v-model="form.is_international_address"
            label="Endereço fora do Brasil / International address" dense />

          <v-row>
            <v-col cols="12" sm="4">
              <v-text-field v-model="form.address_postal_code"
                :label="form.is_international_address ? 'Zip Code' : 'CEP'" outlined dense
                :rules="postalCodeRules" @blur="fetchCep" />
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field v-model="form.address_street" label="Rua/Avenida" outlined dense
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field v-model="form.address_number" label="Número" outlined dense
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field v-model="form.address_complement" label="Complemento" outlined dense />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field v-model="form.address_neighborhood" label="Bairro" outlined dense
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field v-model="form.address_city" label="Cidade" outlined dense
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-autocomplete v-if="!form.is_international_address" v-model="form.address_state"
                :items="states" item-text="name" item-value="sigla" label="Estado" outlined dense
                :rules="[$rules.required]" />
              <v-text-field v-else v-model="form.address_state" label="Estado/Província" outlined dense
                :rules="[$rules.required]" />
            </v-col>
          </v-row>

          <v-file-input v-model="documentFile" label="Atualizar documento principal (opcional)"
            accept="application/pdf,image/png,image/jpeg" outlined dense prepend-icon="" />

          <v-alert v-if="representativeMessage" type="info" outlined dense>
            <div>{{ representativeMessage }}</div>
            <v-btn small outlined color="primary" class="mt-3" @click="manageRepresentatives">
              Gerenciar representantes
            </v-btn>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="loading" @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn color="primary" :loading="loading" @click="save">Salvar dados</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { cpf, cnpj } from 'cpf-cnpj-validator'
import states from '@/common/data/states-list.data'
import countries from '@/common/data/country-list.data'

export default {
  name: 'UserProfileEditModal',
  props: {
    value: { type: Boolean, default: false }
  },
  data: () => ({
    valid: false,
    loading: false,
    states,
    countries,
    documentFile: null,
    representatives: [],
    form: {
      name: '',
      registration: '',
      phone_number: '',
      nationality: '',
      address_postal_code: '',
      address_street: '',
      address_number: '',
      address_complement: '',
      address_neighborhood: '',
      address_city: '',
      address_state: '',
      is_international_address: false
    }
  }),
  computed: {
    formattedRegistration() {
      const value = String(this.form.registration || '').replace(/\D/g, '')
      return value.length === 14 ? cnpj.format(value) : cpf.format(value)
    },
    isCompany() {
      return String(this.form.registration || '').replace(/\D/g, '').length === 14
    },
    postalCodeRules() {
      return this.form.is_international_address
        ? [this.$rules.required]
        : [this.$rules.required, this.$rules.cep]
    },
    representativeMessage() {
      if (!this.isCompany) return ''
      if (!this.representatives.length) return 'Cadastre um representante legal em Minha Conta para continuar.'
      if (this.representatives.some(item => Number(item.status) === 1)) return ''
      if (this.representatives.some(item => Number(item.status) === 0)) {
        return 'O representante legal está em análise. A associação e a adesão serão liberadas após a aprovação.'
      }
      return 'É necessário possuir um representante legal aprovado para continuar.'
    }
  },
  watch: {
    value(open) {
      if (open) this.load()
    }
  },
  methods: {
    manageRepresentatives() {
      this.$emit('input', false)
      this.$router.push('/representantes')
    },
    async load() {
      this.loading = true
      try {
        const response = await axios.get('/api/users/info-assicated-lawyer')
        const user = response.data.content || {}
        Object.keys(this.form).forEach(key => {
          this.form[key] = user[key] === null || user[key] === undefined ? this.form[key] : user[key]
        })
        this.form.is_international_address = Boolean(Number(user.is_international_address))
        this.representatives = user.representatives || []
      } finally {
        this.loading = false
      }
    },
    async fetchCep() {
      if (this.form.is_international_address) return
      const cep = String(this.form.address_postal_code || '').replace(/\D/g, '')
      if (cep.length !== 8) return
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`)
      if (response.data && !response.data.erro) {
        this.form.address_street = response.data.logradouro
        this.form.address_neighborhood = response.data.bairro
        this.form.address_city = response.data.localidade
        this.form.address_state = response.data.uf
      }
    },
    async save() {
      if (!this.$refs.form.validate()) return
      this.loading = true
      try {
        const payload = new FormData()
        Object.keys(this.form).forEach(key => {
          let value = this.form[key]
          if (key === 'address_postal_code' && !this.form.is_international_address) {
            value = String(value || '').replace(/\D/g, '')
          }
          payload.append(key, value === null || value === undefined ? '' : value)
        })
        if (this.documentFile) payload.append('file', this.documentFile)

        const response = await axios.patch('/api/users/me/profile', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.$emit('saved', response.data.content)
        this.$emit('input', false)
        this.$dialog.notify.success('Dados cadastrais atualizados com sucesso!')
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
        this.$dialog.notify.error(message || 'Não foi possível atualizar os dados cadastrais.')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
